import { useEffect, useState } from 'react'
import { Drawer, Field, notify } from '@/kit/ui'
import { codesText, KIT_FIELDS, LENDER_KEY, LIBRARY_KEY, parseCodes } from '@/lib/fieldLibrary'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import type { ApiAnswer, ApiField, LenderApi } from '@/types'

const ANSWERS: { value: ApiAnswer; label: string }[] = [
  { value: 'none', label: 'No offer (dedupe, status, token …)' },
  { value: 'one', label: 'One offer (journey 2)' },
  { value: 'list', label: 'A list of offers + lenders that did not match (an LSP, journey 3)' },
]

type Row = { key: string; required: boolean; lenderKey: string; codes: string }
const toRow = (f: ApiField): Row => ({ key: f.key, required: f.required, lenderKey: f.lenderKey, codes: codesText(f.codes) })

/** What is wrong with the rows, or null; and the fields to save. */
function check(rows: Row[]): { fields: ApiField[]; error: string | null } {
  const fields: ApiField[] = []
  for (const r of rows) {
    const name = r.key || '(no key)'
    if (!LIBRARY_KEY.test(r.key)) return { fields, error: `${name}: a library key is letters and digits, starting lower-case (employerName)` }
    if (!LENDER_KEY.test(r.lenderKey)) return { fields, error: `${name}: the lender's name for it is letters, digits, _ and . (a.b nests)` }
    if (fields.some((f) => f.key === r.key)) return { fields, error: `${r.key} is there twice: one row per key` }
    if (fields.some((f) => f.lenderKey === r.lenderKey)) return { fields, error: `two fields go to the lender's ${r.lenderKey}` }
    const { codes, error } = parseCodes(r.codes)
    if (error) return { fields, error: `${r.key}: ${error}` }
    fields.push({ key: r.key, required: r.required, lenderKey: r.lenderKey, codes })
  }
  return { fields, error: null }
}

/**
 * J2 · tab 3: "Fields this API needs". What the API answers, and each extra field beyond the standard input: our Apply
 * now library key, required or not, the lender's own name for it and our option value → the lender's code. The batch
 * code gets it as ctx.fields / ctx.code() / ctx.lenderFields(input.extra); the directory tells Apply now which keys a
 * live endpoint needs (an approver sees a warning when a journey's form does not ask one).
 */
export function ApiFieldsDrawer({ api, onClose }: { api: LenderApi | null; onClose: () => void }) {
  const { user } = useCurrentUser()
  const updateApi = useCodeStore((s) => s.updateApi)
  const [answer, setAnswer] = useState<ApiAnswer>('none')
  const [rows, setRows] = useState<Row[]>([])
  useEffect(() => {
    if (!api) return
    setAnswer(api.answer)
    setRows(api.fields.map(toRow))
  }, [api])

  const { fields, error } = check(rows)
  const set = (i: number, patch: Partial<Row>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const save = () => {
    if (!api || error) return
    updateApi(api.id, { answer, fields }, user.email)
    notify.success('Fields saved', `${api.name}: ${fields.length} field${fields.length === 1 ? '' : 's'}, answers ${answer}.`)
    onClose()
  }

  return (
    <Drawer
      isOpen={api !== null}
      onClose={onClose}
      title="Fields this API needs"
      subtitle={api ? `${api.name} · ${api.method} ${api.endpoint}` : ''}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!!error} onClick={save} data-testid="api-fields-save">Save</button>
        </div>
      }
    >
      <Field label="This API answers" help="Apply now offers journey 3's pages (Your offers) only for an endpoint whose workflow calls an API that answers a list">
        {(id) => <select id={id} className="input" value={answer} onChange={(e) => setAnswer(e.target.value as ApiAnswer)} data-testid="api-answer">{ANSWERS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</select>}
      </Field>
      <p className="mt-3 text-2xs text-muted">Only the fields beyond the standard input (name, mobile, PAN, date of birth, email, income, work type, PIN code). Our option values are the field library's (App admin → Apply now → Field library); leave the codes empty to send the value as it is. A yes / no answer uses the values <code>yes</code> and <code>no</code>.</p>
      <datalist id="kit-field-keys">{KIT_FIELDS.map((f) => <option key={f.key} value={f.key}>{`${f.label} · ${f.group}`}</option>)}</datalist>
      <div className="mt-2 space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="card grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={`api-field-${i}`}>
            <Field label="Our library key" help={KIT_FIELDS.find((f) => f.key === r.key)?.label ?? 'e.g. employerName'}>
              {(id) => <input id={id} className="input mono" list="kit-field-keys" value={r.key} onChange={(e) => { const key = e.target.value.trim(); set(i, { key, lenderKey: !r.lenderKey || r.lenderKey === r.key ? key : r.lenderKey }) }} />}
            </Field>
            <Field label="The lender's name for it" help="a.b nests: address.residenceType">
              {(id) => <input id={id} className="input mono" value={r.lenderKey} onChange={(e) => set(i, { lenderKey: e.target.value.trim() })} />}
            </Field>
            <Field label="Our value = the lender's code" help="One per line, e.g. rented = RENTED">
              {(id) => <textarea id={id} className="input mono" rows={3} value={r.codes} onChange={(e) => set(i, { codes: e.target.value })} />}
            </Field>
            <div className="flex flex-col justify-between gap-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={r.required} onChange={(e) => set(i, { required: e.target.checked })} /> Required by the lender</label>
              <button type="button" className="btn btn-ghost btn-sm self-start" onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-secondary btn-sm mt-3" onClick={() => setRows([...rows, { key: '', required: true, lenderKey: '', codes: '' }])} data-testid="api-field-add">+ Add a field</button>
      {error && <p className="field-error mt-2">{error}</p>}
    </Drawer>
  )
}
